import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {ELEMENT} from '../../incase-domain/elements/base/ElementTypes';
import {IPresentation} from '../../incase-domain/presentation';
import {ISlide} from '../../incase-domain/slide';
import {fetchPresentationForSlidePreview} from '../../service/presentation.service';
import {
    updateFullPresentationAction,
    updatePresentationAction,
    updatePresentationHasSlidesAction,
    updatePresentationRunning,
} from '../../store/actions/dashboard.actions';
import {setDesktopViewSelected, setSelectedDeviceTypeAction} from '../../store/actions/layout.actions';
import {resetPresentationReducerAction, updateCurrentSlide} from '../../store/actions/presentation.actions';
import {updateSpinnerLoadingAction} from '../../store/actions/spinner.actions';
import {loadedPresentationSelector} from '../../store/selectors/presentation.selectors';
import PresentationView from '../../components/presentationView/PresentationView';
import {SlidePreviewCaptureProvider} from '../../context/SlidePreviewCaptureContext';

export const SLIDE_PREVIEW_APPLY_EVENT = 'slide-preview-apply';

const HTML_FRAME_TIMEOUT_MS = 2000;
const READY_TIMEOUT_MS = 6000;

function countHtmlFrames(slide: ISlide): number {
    return (slide.elements || []).reduce((count, element) => {
        if (element.type === ELEMENT.HTML) {
            return element.text ? count + 1 : count;
        }
        if (element.type === ELEMENT.FLIP_CARD) {
            const sides = Object.values(element.flipCardElement || {});
            return count + sides.filter((side) => side?.type === ELEMENT.HTML && side.text).length;
        }
        return count;
    }, 0);
}

function SlidePreviewCapturePage(): JSX.Element {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [ready, setReady] = useState(false);
    const [applyGeneration, setApplyGeneration] = useState(0);
    const loadedPresentation = useSelector(loadedPresentationSelector);

    const presentationId = searchParams.get('presentationId') || '';
    const slideId = searchParams.get('slideId') || '';
    const exp = searchParams.get('exp') || '';
    const sig = searchParams.get('sig') || '';
    const [activeSlideId, setActiveSlideId] = useState(slideId);

    const previewSlide = loadedPresentation?.slides?.[0];
    const matchedSlideId = previewSlide && String(previewSlide._id) === String(activeSlideId) ? String(previewSlide._id) : '';
    const htmlFrameCount = useMemo(
        () => (matchedSlideId && previewSlide ? countHtmlFrames(previewSlide) : 0),
        [matchedSlideId, previewSlide]
    );

    const applyPresentation = useCallback(
        (presentation: IPresentation): void => {
            const nextSlideId = String(presentation.slides?.[0]?._id || '');
            setReady(false);
            setActiveSlideId(nextSlideId);
            // Bump every apply so the ready wait restarts even when the slide id is unchanged
            // (initial URL fetch + Playwright apply event can race).
            setApplyGeneration((generation) => generation + 1);
            dispatch(updatePresentationAction({...presentation, found: true}));
            dispatch(updateFullPresentationAction(presentation));
            dispatch(updatePresentationHasSlidesAction(true));
            dispatch(updateCurrentSlide(0));
            dispatch(updatePresentationRunning(true));
        },
        [dispatch]
    );

    useLayoutEffect(() => {
        setReady(false);
        setActiveSlideId(slideId);
    }, [presentationId, slideId, exp, sig]);

    useEffect(() => {
        dispatch(updateSpinnerLoadingAction(false));
        dispatch(setDesktopViewSelected(true));
        dispatch(setSelectedDeviceTypeAction(DeviceType.DESKTOP));

        const onApply = (event: Event): void => {
            const presentation = (event as CustomEvent<IPresentation>).detail;
            if (!presentation?.slides?.[0]) {
                return;
            }
            applyPresentation(presentation);
        };
        window.addEventListener(SLIDE_PREVIEW_APPLY_EVENT, onApply);

        return () => {
            window.removeEventListener(SLIDE_PREVIEW_APPLY_EVENT, onApply);
        };
    }, [applyPresentation, dispatch]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!presentationId || !slideId || !exp || !sig) {
                return;
            }
            try {
                const presentation: IPresentation = await fetchPresentationForSlidePreview(presentationId, slideId, exp, sig);
                if (cancelled) {
                    return;
                }
                applyPresentation(presentation);
            } catch (error) {
                // do nothing
            }
        })();

        return () => {
            cancelled = true;
            dispatch(resetPresentationReducerAction());
        };
    }, [applyPresentation, dispatch, presentationId, slideId, exp, sig]);

    useEffect(() => {
        if (!matchedSlideId || !applyGeneration) {
            return undefined;
        }

        let cancelled = false;
        const startedAt = performance.now();

        const markReady = (): void => {
            if (!cancelled) {
                setReady(true);
            }
        };

        const tick = (): void => {
            if (cancelled) {
                return;
            }

            const elapsed = performance.now() - startedAt;
            const canvas = document.querySelector('#canvas-holder');
            const frames = Array.from(document.querySelectorAll<HTMLElement>('#canvas-holder [data-html-loaded]'));
            const htmlReady =
                htmlFrameCount === 0 ||
                (frames.length >= htmlFrameCount && frames.every((frame) => frame.dataset.htmlLoaded === 'true')) ||
                elapsed > HTML_FRAME_TIMEOUT_MS;

            if ((canvas && htmlReady) || elapsed > READY_TIMEOUT_MS) {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(markReady);
                });
                return;
            }

            window.requestAnimationFrame(tick);
        };

        window.requestAnimationFrame(tick);

        const timeout = window.setTimeout(markReady, READY_TIMEOUT_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [matchedSlideId, applyGeneration, htmlFrameCount]);

    return (
        <SlidePreviewCaptureProvider>
            <div
                className="p-player p-player--desktop p-player--preview"
                data-slide-preview-presentation-id={loadedPresentation?._id || presentationId}
                data-slide-preview-id={activeSlideId}
                data-slide-preview-ready={ready ? 'true' : 'false'}
            >
                {loadedPresentation?.slides?.[0] ? <PresentationView /> : null}
            </div>
        </SlidePreviewCaptureProvider>
    );
}

export default SlidePreviewCapturePage;
